import '@fontsource-variable/plus-jakarta-sans';
import { lazy, Suspense } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import type { Shadows } from '@mui/material/styles';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Public Pages
import HomePage from './pages/public/HomePage';
import ServicesPage from './pages/public/ServicesPage';
import ServiceDetailPage from './pages/public/ServiceDetailPage';
import KooperationPage from './pages/public/KooperationPage';
import PreisePage from './pages/public/PreisePage';
import PriceCategoryPage from './pages/public/PriceCategoryPage';
import ContactPage from './pages/public/ContactPage';
import ImpressumPage from './pages/public/ImpressumPage';
import DatenschutzPage from './pages/public/DatenschutzPage';
import AgbPage from './pages/public/AgbPage';
import UeberUnsPage from './pages/public/UeberUnsPage';
import TeamMemberPage from './pages/public/TeamMemberPage';
import NotFoundPage from './pages/public/NotFoundPage';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Admin-Bereich – eigenes Bundle, wird nur unter /admin geladen
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminServices = lazy(() => import('./pages/admin/AdminServices'));
const AdminPreise = lazy(() => import('./pages/admin/AdminPreise'));
const AdminContacts = lazy(() => import('./pages/admin/AdminContacts'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminUeberUns = lazy(() => import('./pages/admin/AdminUeberUns'));
const AdminPages = lazy(() => import('./pages/admin/AdminPages'));
const AdminPageEditor = lazy(() => import('./pages/admin/AdminPageEditor'));
const AdminTexte = lazy(() => import('./pages/admin/AdminTexte'));
const AdminMedia = lazy(() => import('./pages/admin/AdminMedia'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminDevices = lazy(() => import('./pages/admin/AdminDevices'));
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));

// Theme — Squarespace-inspired: clean, minimal, editorial
const theme = createTheme({
  palette: {
    primary: {
      main: '#1737bc',
      light: '#2a4fd4',
      dark: '#0f2899',
    },
    secondary: {
      main: '#ffffff',
      light: '#f5f5f5',
      dark: '#e0e0e0',
    },
    background: {
      default: '#ffffff',
      paper: '#f7f7f7',
    },
    text: {
      primary: '#444444',
      secondary: '#6b6b6b',
    },
    divider: '#e8e8e8',
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Helvetica Neue", "Arial", sans-serif',
    h1: { fontWeight: 700, fontSize: '4rem', letterSpacing: '-1.5px', lineHeight: 1.1 },
    h2: { fontWeight: 700, fontSize: '2.75rem', letterSpacing: '-1px', lineHeight: 1.2 },
    h3: { fontWeight: 600, fontSize: '2rem', letterSpacing: '-0.5px', lineHeight: 1.3 },
    h4: { fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.3px' },
    h5: { fontWeight: 600, fontSize: '1.2rem' },
    h6: { fontWeight: 500, fontSize: '1rem' },
    body1: { fontSize: '1rem', lineHeight: 1.7, color: '#444444' },
    body2: { fontSize: '0.9rem', lineHeight: 1.6 },
    overline: { fontWeight: 600, letterSpacing: '2px', fontSize: '0.75rem' },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.3px' },
  },
  shape: { borderRadius: 4 },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.06)',
    '0 2px 6px rgba(0,0,0,0.08)',
    '0 4px 12px rgba(0,0,0,0.08)',
    '0 8px 24px rgba(0,0,0,0.10)',
    '0 12px 32px rgba(0,0,0,0.12)',
    ...Array(19).fill('none'),
  ] as Shadows,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          padding: '12px 28px',
          fontSize: '0.95rem',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '&:focus-visible': {
            outline: '3px solid #1737bc',
            outlineOffset: '2px',
          },
        },
        containedPrimary: {
          backgroundColor: '#1737bc',
          color: '#ffffff',
          '&:hover': { backgroundColor: '#0f2899', transform: 'translateY(-1px)' },
        },
        outlinedPrimary: {
          borderColor: '#1737bc',
          borderWidth: '1.5px',
          color: '#1737bc',
          '&:hover': { borderWidth: '1.5px', backgroundColor: '#1737bc', color: '#ffffff' },
        },
        outlinedSecondary: {
          borderColor: 'rgba(255,255,255,0.7)',
          borderWidth: '1.5px',
          color: '#ffffff',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(255,255,255,0.12)',
            borderColor: '#ffffff',
          },
        },
        text: {
          '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          boxShadow: 'none',
          border: '1px solid #e8e8e8',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { borderColor: '#c0c0c0', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: { root: { boxShadow: 'none' } },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '3px solid #1737bc',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '3px solid #1737bc',
            outlineOffset: '2px',
            borderRadius: 2,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 3, fontWeight: 500 } },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            '& fieldset': { borderColor: '#d0d0d0' },
            '&:hover fieldset': { borderColor: '#1737bc' },
            '&.Mui-focused fieldset': { borderColor: '#1737bc', borderWidth: '1.5px' },
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LanguageProvider>
          <Router>
            <Suspense
              fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                  <CircularProgress />
                </Box>
              }
            >
            <Routes>
              {/* Public Routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/services/:id" element={<ServiceDetailPage />} />
                <Route path="/kooperation" element={<KooperationPage />} />
                <Route path="/preise" element={<PreisePage />} />
                <Route path="/preise/:category" element={<PriceCategoryPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/datenschutz" element={<DatenschutzPage />} />
                <Route path="/agb" element={<AgbPage />} />
                <Route path="/ueber-uns" element={<UeberUnsPage />} />
                {/* Entfernte Seite */}
                <Route path="/power-platform" element={<Navigate to="/services" replace />} />
                <Route path="*" element={<NotFoundPage />} />
                <Route path="/ueber-uns/team/:slug" element={<TeamMemberPage />} />
              </Route>

              {/* Admin Login */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Admin Routes (Protected) */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="kooperation" element={<Navigate to="/admin/seiten/page_kooperation" replace />} />
                <Route path="seiten" element={<AdminPages />} />
                <Route path="seiten/:key" element={<AdminPageEditor />} />
                <Route path="texte" element={<AdminTexte />} />
                <Route path="medien" element={<AdminMedia />} />
                <Route path="benutzer" element={<AdminUsers />} />
                <Route path="geraete" element={<AdminDevices />} />
                <Route path="firmendaten" element={<Navigate to="/admin/seiten/settings" replace />} />
                <Route path="preise" element={<AdminPreise />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="ueber-uns" element={<AdminUeberUns />} />
              </Route>
            </Routes>
            </Suspense>
          </Router>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
